/**
 * Verbatim prototype copy for the page editor (C1-editor.md §1.3–§1.23,
 * `wkEditorVals` / `wkWysiMarks` / `wkSlashList` in Inbox.dc.html) plus the
 * few production-only strings the prototype has no copy for (marked EXTRA).
 */

export const EDITOR_COPY = Object.freeze({
    statusDraft: 'Editing a draft',
    savedAsMarkdown: ' · saved as Markdown',
    cancel: 'Cancel',
    saveDraft: 'Save draft',
    publish: 'Publish',
    confirmDiscard: 'Discard your changes?',
    keepEditing: 'Keep editing',
    discard: 'Discard',
    modeWrite: 'Write',
    modeMarkdown: 'Markdown',
    markdownToolbarNote: "You're editing the stored Markdown. Switch to Write for the formatting buttons.",
    markdownIntro: "This is the page exactly as it's stored. Imported files open here unchanged, so you can edit them as written.",
    titlePlaceholder: 'Page title',
    requiredReading: ' · Required reading',
    importedFrom: ' · Imported from ',
    linkInsert: 'Insert',
    linkCancel: 'Cancel',
    slashHeader: 'INSERT · type to filter, Enter to pick',
    slashEmpty: 'Nothing matches. Keep typing, or press Escape.',
    // Placeholders (wkWysiMarks).
    phIntro: 'Just start writing. Type # and a space for a heading, - for a list, or / for tables, callouts and more.',
    phHeading: 'Heading',
    phAfterHeading: 'Write here, or type / for blocks',
    // Toasts (wkSavePage).
    toastDraftSaved: 'Draft saved. Only you and admins can see it.',
    toastPublishedDraft: "Published. The AI can use this page in about a minute, once it's indexed.",
    // EXTRA — production states with no prototype design.
    saving: 'Saving…',
    publishing: 'Publishing…',
    overwrite: 'Replace it with my version',
    noAccessTitle: "You can't edit this page",
    noAccessDetail: 'Your role can read this page, but only editors and admins can change it.',
    backToPage: 'Back to the page',
    loading: 'Opening the editor…',
});

/** "Editing v4, publishes as v5" (published) / "Editing a draft" (draft). */
export function editorStatus(page) {
    if (!page || page.status === 'draft') return EDITOR_COPY.statusDraft;
    const v = Number(page.version) || 0;
    return `Editing v${v}, publishes as v${v + 1}`;
}

/** "1 word · saved as Markdown" / "12 words · saved as Markdown". */
export function editorStats(words) {
    const n = Number(words) || 0;
    return `${n}${n === 1 ? ' word' : ' words'}${EDITOR_COPY.savedAsMarkdown}`;
}

/** "Publish" (draft) / "Publish v5" (published v4). */
export function publishLabel(page) {
    if (!page || page.status === 'draft') return EDITOR_COPY.publish;
    return `Publish v${(Number(page.version) || 0) + 1}`;
}

/** Meta line: "Sales › Pricing · Required reading · Imported from refund-policy.md". */
export function editorMeta(page) {
    if (!page) return '';
    let out = page.category_name || '';
    if (page.subcategory_id && page.subcategory_name) out += ` › ${page.subcategory_name}`;
    if (page.required_ack) out += EDITOR_COPY.requiredReading;
    const file = page.imported_from && page.imported_from.filename;
    if (file) out += EDITOR_COPY.importedFrom + file;
    return out;
}

/**
 * The post-save toast (prototype wkSavePage). `wasDraft` is the page status
 * when the editor opened; `saved` is the API response (full Page, plus
 * `reconfirm_count` on publish = people who still have to confirm, the
 * publisher being auto-acknowledged).
 *
 * Divergence: the prototype named the whole audience ("the 24 people who must
 * read it"); the API reports how many still have to confirm, which is the
 * number that is actually asked. One person reads "the 1 person … is asked";
 * nobody left to ask falls back to the not-required sentence.
 */
export function saveToast({ publish, wasDraft, saved }) {
    if (!publish) return EDITOR_COPY.toastDraftSaved;
    if (wasDraft) return EDITOR_COPY.toastPublishedDraft;
    const v = Number(saved && saved.version) || 0;
    const base = `Published v${v}. The AI switches to it in about a minute`;
    const n = Number(saved && saved.reconfirm_count) || 0;
    if (!(saved && saved.required_ack) || n < 1) return `${base}.`;
    return n === 1
        ? `${base}, and the 1 person who must read it is asked to confirm v${v}.`
        : `${base}, and the ${n} people who must read it are asked to confirm v${v}.`;
}

/** EXTRA (no prototype design): the 409 version_conflict notice. */
export function conflictMessage(currentVersion, publish) {
    const v = Number(currentVersion) || 0;
    const head = v > 0
        ? `Someone published v${v} while you were editing.`
        : 'Someone changed this page while you were editing.';
    return publish
        ? `${head} Your changes are still here. Publishing now replaces v${v} with your version.`
        : `${head} Your changes are still here. Saving now keeps your version as the draft.`;
}

/** Toolbar groups (wkTbStyles / wkTbInline / wkTbLists / wkTbInserts), verbatim tooltips. */
export const TOOLBAR_GROUPS = Object.freeze([
    [
        { kind: 'p', label: 'Text', title: 'Normal text (Ctrl/Cmd+Alt+0)' },
        { kind: 'h1', label: 'H1', title: 'Heading 1 (# then space)' },
        { kind: 'h2', label: 'H2', title: 'Heading 2 (## then space)' },
        { kind: 'h3', label: 'H3', title: 'Heading 3 (### then space)' },
    ],
    [
        { kind: 'bold', label: 'B', title: 'Bold (Ctrl/Cmd+B, or **text**)', variant: 'bold' },
        { kind: 'italic', label: 'I', title: 'Italic (Ctrl/Cmd+I, or _text_)', variant: 'italic' },
        { kind: 'strike', label: 'S', title: 'Strikethrough (Ctrl/Cmd+Shift+X, or ~~text~~)', variant: 'strike' },
        { kind: 'code', label: '</>', title: 'Inline code (Ctrl/Cmd+E, or a backtick pair)', variant: 'code' },
        { kind: 'link', label: 'Link', title: 'Link (Ctrl/Cmd+K)' },
    ],
    [
        { kind: 'ul', icon: 'lucide:list', title: 'Bulleted list (- then space)' },
        { kind: 'ol', icon: 'lucide:list-ordered', title: 'Numbered list (1. then space)' },
        { kind: 'task', icon: 'lucide:square-check-big', title: 'Checklist ([] then space)' },
        { kind: 'quote', icon: 'lucide:quote', title: 'Quote (> then space)' },
        { kind: 'note', icon: 'lucide:info', title: 'Note callout' },
        { kind: 'warn', icon: 'lucide:alert-triangle', title: 'Warning callout' },
    ],
    [
        { kind: 'pre', icon: 'lucide:code-xml', title: 'Code block (three backticks, then Enter)' },
        { kind: 'table', icon: 'lucide:columns-3', title: 'Table (Tab moves between cells)' },
        { kind: 'image', icon: 'lucide:image', title: 'Image' },
        { kind: 'video', icon: 'lucide:play', title: 'Video (Loom, YouTube, Vimeo)' },
        { kind: 'hr', icon: 'lucide:minus', title: 'Divider (--- then Enter)' },
    ],
]);

/** Link / image / video bar (wkLinkLabel / wkLinkPh). */
export const LINK_BAR = Object.freeze({
    link: { label: 'Link address', placeholder: 'Paste or type a link, then press Enter' },
    image: { label: 'Image address', placeholder: 'Paste an image link (.png, .jpg, .gif)' },
    video: { label: 'Video link', placeholder: 'Paste a Loom, YouTube or Vimeo link' },
});

/**
 * Right-rail cheat sheet rows (code, label). The no-break space is built
 * from its char code so no editor tooling can turn it into an invisible literal.
 */
const NBSP = String.fromCharCode(160);
export const CHEAT_SHEET = Object.freeze({
    eyebrow: 'TYPE MARKDOWN, SEE FORMATTING',
    intro: 'Type the marks at the start of a line and they turn into formatting as you go.',
    rows: Object.freeze([
        [`# ${NBSP}## ${NBSP}### + space`, 'Headings'],
        ['- + space', 'Bulleted list'],
        ['1. + space', 'Numbered list'],
        ['[] + space', 'Checklist'],
        ['> + space', 'Quote'],
        ['**bold** _italic_', 'Bold, italic'],
        [`\`code\` ${NBSP}~~strike~~`, 'Code, strikethrough'],
        ['``` + Enter', 'Code block'],
        ['--- + Enter', 'Divider'],
        ['/', 'Tables, callouts, images, video'],
        ['Ctrl/Cmd + B · I · K', 'Bold, italic, link'],
        // EXTRA (accessibility): Tab stays in the page, so Esc is the way out.
        ['Esc', 'Leave the editor'],
    ]),
    footer1: "It's one page. Enter starts a new line, arrows move anywhere, and you can select across lines to format them. Paste Markdown and it formats itself. Switch to Markdown to see or edit the stored file.",
    footer2: 'Write numbers, names and rules exactly. The AI rewrites wording for readers but keeps those as you wrote them.',
});
